/**
 * Error Tracking Utilities
 *
 * Helper functions for manual error tracking with Sentry
 */

import { Sentry } from "../config/sentry";

/**
 * Capture an exception manually
 *
 * @example
 * try {
 *   // some code
 * } catch (error) {
 *   captureException(error, {
 *     tags: { feature: 'calculator' },
 *     level: 'error'
 *   });
 * }
 */
export function captureException(
	error: Error | unknown,
	captureContext?: Parameters<typeof Sentry.captureException>[1],
) {
	console.error("Error captured:", error);
	return Sentry.captureException(error, captureContext);
}

/**
 * Capture a message manually
 *
 * @example
 * captureMessage('Payment processing failed', 'warning', {
 *   tags: { userId: '123', feature: 'payments' }
 * });
 */
export function captureMessage(
	message: string,
	level: "fatal" | "error" | "warning" | "log" | "info" | "debug" = "info",
	captureContext?: {
		tags?: Record<string, string>;
		extra?: Record<string, unknown>;
	},
) {
	if (captureContext) {
		return Sentry.captureMessage(message, {
			level,
			tags: captureContext.tags,
			extra: captureContext.extra,
		});
	}
	return Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 * Breadcrumbs are like a trail that leads up to an error
 *
 * @example
 * addBreadcrumb({
 *   category: 'user-action',
 *   message: 'User clicked calculate button',
 *   level: 'info',
 *   data: { amount: 1000, currency: 'USD' }
 * });
 */
export function addBreadcrumb(
	breadcrumb: Parameters<typeof Sentry.addBreadcrumb>[0],
) {
	Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Set user context
 * This helps identify which user encountered an error
 *
 * @example
 * setUser({
 *   id: '123',
 *   email: 'user@example.com',
 *   username: 'john_doe'
 * });
 */
export function setUser(user: Parameters<typeof Sentry.setUser>[0]) {
	Sentry.setUser(user);
}

/**
 * Clear user context
 * Call this when user logs out
 *
 * @example
 * clearUser();
 */
export function clearUser() {
	Sentry.setUser(null);
}

/**
 * Set custom context
 * Add additional data to error reports
 *
 * @example
 * setContext('transaction', {
 *   id: 'tx-123',
 *   amount: 1000,
 *   status: 'pending'
 * });
 */
export function setContext(key: string, context: Record<string, unknown>) {
	Sentry.setContext(key, context);
}

/**
 * Set a tag
 * Tags are searchable in Sentry
 *
 * @example
 * setTag('payment_method', 'credit_card');
 */
export function setTag(key: string, value: string) {
	Sentry.setTag(key, value);
}

/**
 * Start a span for performance monitoring
 * Note: Use with Sentry.startSpan() for modern Sentry SDK
 *
 * @example
 * Sentry.startSpan({
 *   name: 'payment-processing',
 *   op: 'payment.process',
 * }, async () => {
 *   // Your code here
 *   await processPayment();
 * });
 */
export function startSpan(
	options: { name: string; op?: string },
	callback: () => Promise<void> | void,
) {
	return Sentry.startSpan(options, callback);
}

/**
 * Wrap async function with error handling
 * Automatically captures errors and sends them to Sentry
 *
 * @example
 * const safeFunction = withErrorHandling(async () => {
 *   // Your async code here
 *   return await fetchData();
 * }, 'fetchData');
 */
export function withErrorHandling<T>(
	fn: () => Promise<T>,
	context?: string,
): () => Promise<T> {
	return async () => {
		try {
			return await fn();
		} catch (error) {
			captureException(error, {
				tags: {
					context: context || "unknown",
				},
			});
			throw error;
		}
	};
}
